import {
  collectAncestorSectionQnames,
  type LeveledControl,
} from './section-visibility.util';

/** What a destination sheet must do to reveal an arriving field-link target. */
export interface FieldLinkArrivalPlan {
  /** Index of the target control, or -1 when the qname is not on this sheet. */
  index: number;
  /** Ancestor section qnames that must be expanded, outermost last. */
  ancestors: string[];
}

/**
 * Resolve where a `?scrollTo=` target lives and what must be opened to reveal it.
 *
 * WHY THIS DELEGATES TO `collectAncestorSectionQnames` RATHER THAN WALKING
 * LEVELS ITSELF — the three destination sheets each hand-rolled their own
 * ancestor walk and all three disagreed: role 9 expanded levels 3 and 4, role 13
 * expanded 2, 3 and 4, role 15 expanded nothing. Reusing the walk that
 * `isRowVisible` / `shouldShowRow` already consult is the only way the set
 * expanded here is guaranteed to be the set that governs visibility. There is no
 * level list left to get wrong.
 *
 * Pass the sheet's own `isSection` predicate: several sheets tag DATA fields with
 * a `ui.level` purely for indentation, and those are not section boundaries.
 *
 * Pure and DI-free, so it unit-tests without TestBed.
 */
export function planFieldLinkArrival<T extends LeveledControl>(
  controls: readonly T[],
  qname: string,
  isSection?: (control: T) => boolean
): FieldLinkArrivalPlan {
  const index = controls.findIndex((control) => control.qname === qname);
  if (index === -1) return { index: -1, ancestors: [] };

  return { index, ancestors: collectAncestorSectionQnames(controls, index, isSection) };
}
